// Centralized API Service

const API_BASE_URL = 'https://legaltrademarksolutions.com/api';
const GEO_API_URL = 'https://legaltrademarksolutions.com/api/GeoLocation/GetGeoLocation';

/**
 * Fetch geolocation data
 */
export const fetchGeolocation = async () => {
  try {
    const response = await fetch(GEO_API_URL, {
      method: 'GET',
      headers: {
        'accept': 'text/plain'
      }
    });
    
    const data = await response.json();
    
    if (data.isSuccess && data.statusCode === 200) {
      return {
        success: true,
        data: data.data
      };
    }
    
    return {
      success: false,
      data: null
    };
  } catch (error) {
    console.error('Geolocation API Error:', error);
    return {
      success: false,
      data: null
    };
  }
};

/**
 * Extract numeric phone number (remove all non-numeric characters)
 */
const extractNumericPhone = (phoneNumber) => {
  return phoneNumber.replace(/\D/g, '');
};

/**
 * Submit lead to API
 * @param {Object} formData - Form data containing name, email, phone, etc.
 * @param {Object} geoData - Geolocation data (optional)
 * @param {string} packageTitle - Package title (optional)
 * @param {number} packageAmount - Package amount (optional)
 */
export const submitLead = async (formData, geoData = null, packageTitle = '', packageAmount = 0) => {
  try {
    // Extract numeric phone number only
    const numericPhone = extractNumericPhone(formData.phone);
    
    // Prepare payload
    const payload = {
      name: formData.name,
      email: formData.email,
      countryDialCode: parseInt(formData.countryCode.replace('+', '')),
      country: formData.countryCode.replace('+', ''), // Will be extracted from country code
      phoneNumber: numericPhone,
      ipAddress: geoData?.ipAddress || '',
      description: formData.message || '',
      pageUrl: window.location.href,
      city: geoData?.cityName || '',
      state: geoData?.regionName || '',
      geoLocation: geoData ? JSON.stringify({
        isSuccess: true,
        data: geoData,
        errorMessage: '',
        statusCode: 200
      }) : '',
      packageTitle: packageTitle,
      packageAmount: packageAmount
    };

    const response = await fetch(`${API_BASE_URL}/Lead/AddLead`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'text/plain'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    // Handle different status codes
    if (response.status === 200 && data.isSuccess && data.data?.customerId) {
      return {
        success: true,
        customerId: data.data.customerId,
        message: data.data.message || 'Lead created successfully',
        redirect: `/application/main?customerid=${data.data.customerId}`
      };
    }

    if (response.status === 401) {
      return {
        success: false,
        error: data.message || 'Unauthorized access',
        statusCode: 401
      };
    }

    if (response.status === 400) {
      return {
        success: false,
        error: data.errorMessage || data.data?.message || 'Invalid data',
        statusCode: 400
      };
    }

    // Handle other errors
    return {
      success: false,
      error: data.errorMessage || 'Something went wrong. Please try again.',
      statusCode: response.status
    };

  } catch (error) {
    console.error('Lead API Error:', error);
    return {
      success: false,
      error: 'Network error. Please check your connection and try again.',
      statusCode: 500
    };
  }
};

/**
 * Get country code from geolocation data
 */
export const getCountryFromGeo = (geoData) => {
  if (geoData && geoData.countryCode) {
    return geoData.countryCode;
  }
  return 'US'; // Default to US
};

/**
 * Find country dial code from country code
 */
export const getDialCodeFromCountryCode = (countryCode, countryCodes) => {
  const country = countryCodes.find(c => c.code === countryCode);
  return country ? country.dial : '+1';
};
